import { twMerge } from "tailwind-merge";
import { PageContainer } from "../../components/pageContainer/PageContainer";
import { ActionPanelContainer } from "../../components/actionPanel/ActionPanelContainer";
import dayjs from "dayjs";
import { ReactElement, useState } from "react";
import { useNavigate } from "react-router";
import {
  DropdownLegacy,
  Icon,
  ProfilePicture,
  SpinnerLegacy,
  DropdownLegacyElement,
} from "../../components";
import { BadgeGreen, BadgeRed, BadgeAmber } from "../../components/badge/Badge";
import { useDashboardContext } from "../../components/dashboard/Dashboard.shared";
import { NavigationBar } from "../../components/navigation/NavigationBar";
import {
  COLLECTION_DETAILS,
  COLLECTIONS,
  ORDER_DETAILS,
} from "../../constants/routes";
import { useCollection } from "../../hooks/api/v2/collections/useCollection";
import { useParamAsNumber } from "../../hooks/useParamAsNumber";
import {
  Card,
  CardContent,
  CardHeader,
  H3,
  H5,
  H6,
  imageUrls,
} from "@sellar-io/components";
import { CancelCollectionDialog } from "./CancelCollectionDialog";
import { CollectionContactDetails } from "./CollectionContactDetails";
import { FinaliseCollectionDialog } from "./FinaliseCollectionDialog";
import { formatCurrency } from "@sellar-io/common";
import { useRemoteFeatureFlags } from "../../hooks/useRemoteFeatureFlags";
import { CollectionGetResponseType } from "@sellar-io/api-v2/types";
import { useCollectionOrderData } from "./useCollectionOrderData";

export function CollectionDetails(): ReactElement {
  const collectionId = useParamAsNumber(COLLECTION_DETAILS, "collectionId");
  const { isTwoPanel } = useDashboardContext();
  const navigate = useNavigate();
  const [cancelModalActive, setCancelModalActive] = useState<boolean>(false);
  const [finaliseModalActive, setFinaliseModalActive] =
    useState<boolean>(false);
  const collection = useCollection(collectionId);

  const status = () => {
    switch (collection.data?.status) {
      case "active":
        return <BadgeGreen icon={"CHECK_CIRCLE"}>Active</BadgeGreen>;
      case "cancelled":
        return <BadgeRed icon={"CLOSE"}>Cancelled</BadgeRed>;
      case "draft":
        return <BadgeAmber icon={"TIMER_SAND_EMPTY"}>Draft</BadgeAmber>;
    }
  };

  const collectionsV2Enabled =
    useRemoteFeatureFlags().isActive("collectionsV2");

  const associatedOrders = collection.data?.Orders;

  const hasOrders =
    collectionsV2Enabled && !!associatedOrders && associatedOrders.length > 0;
  const serviceProvider = collection.data?.fulfillmentType.toUpperCase();

  const imageURl =
    serviceProvider === "APC" ? imageUrls.apcLogo : imageUrls.upsLogo;
  return (
    <>
      <NavigationBar
        title={"Collection details"}
        leftIcon={"CHEVRON_LEFT"}
        onClickLeftIcon={isTwoPanel ? undefined : () => navigate(COLLECTIONS)}
      />
      <PageContainer className={"flex flex-col gap-4"}>
        {collection.isLoading || !collection.data ? (
          <SpinnerLegacy />
        ) : (
          <div className={"flex flex-col gap-2"}>
            <Card className="bg-white">
              <CardHeader
                className={twMerge(
                  "bg-white",
                  "flex items-center justify-between p-4",
                )}
              >
                <div className="flex flex-col">
                  <H5>{serviceProvider} Collection</H5>
                  <H6 className={"select-text"}>
                    Reference #: {collection.data.prn ?? "DRAFT"}
                  </H6>
                </div>
                <div className="flex flex-row items-center gap-2">
                  {status()}
                  {collection.data.status !== "cancelled" && (
                    <DropdownLegacy className={"print:hidden"}>
                      {collection.data.status === "draft" && (
                        <DropdownLegacyElement
                          onClick={() => setFinaliseModalActive(true)}
                          className={"text-emerald-400"}
                        >
                          Finalise Collection
                        </DropdownLegacyElement>
                      )}
                      {collection.data.fulfillmentType === "ups" && (
                        <DropdownLegacyElement
                          onClick={() => setCancelModalActive(true)}
                          className={"text-red-400"}
                        >
                          Cancel Collection
                        </DropdownLegacyElement>
                      )}
                    </DropdownLegacy>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <ActionPanelContainer type={"default"} className={"m-4 p-4"}>
                  <div className="flex flex-col items-center gap-2">
                    <img
                      alt={serviceProvider}
                      src={imageURl}
                      className={"h-[48px] w-[48px]"}
                    />
                    <H3>
                      {" "}
                      {dayjs(collection.data.collectionDate).format(
                        "dddd DD MMMM",
                      )}
                    </H3>
                    {collection.data.status === "active" ? (
                      <>
                        <div className="flex flex-col text-center">
                          <p>Collecting between</p>
                          <H3>
                            {collection.data.earliestCollectionTime} -{" "}
                            {collection.data.latestCollectionTime}
                          </H3>
                        </div>
                      </>
                    ) : (
                      <p>This collection has been cancelled</p>
                    )}
                  </div>
                </ActionPanelContainer>
              </CardContent>
            </Card>
            {hasOrders && (
              <Card className="bg-white">
                <CardHeader
                  className={twMerge(
                    "bg-white",
                    "flex items-center justify-between p-4",
                  )}
                >
                  <H5>Orders</H5>
                </CardHeader>
                <CardContent className={"p-4"}>
                  <div className="flex gap-2 overflow-x-auto">
                    {collection.data.Orders.map((order, idx) => (
                      <OrderCard key={idx} order={order} />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            <Card className="bg-white">
              <CardHeader
                className={twMerge(
                  "bg-white",
                  "flex items-center justify-between p-4",
                )}
              >
                <H5>Collection info</H5>
              </CardHeader>
              <CardContent className={"p-4"}>
                <div className="grid md:grid-cols-2">
                  <div className="col-span-1 flex flex-col gap-4">
                    <div className="">
                      <H5>Service provider</H5>
                      <H6>{serviceProvider}</H6>
                    </div>
                    <div className="">
                      <H5>Total weight</H5>
                      <H6>{collection.data.totalWeight}Kg</H6>
                    </div>
                  </div>
                  <div className="col-span-1 flex flex-col gap-4">
                    <div className="">
                      <H5>Number of packages</H5>
                      <H6>{collection.data.numberOfPackages}</H6>
                    </div>
                    {serviceProvider && serviceProvider === "UPS" && (
                      <div className="">
                        <H5>Contains items over 32kg</H5>
                        <H6>{collection.data.isOverweight ? "Yes" : "No"}</H6>
                      </div>
                    )}
                  </div>
                </div>
                {collection.data.specialInstructions && (
                  <ActionPanelContainer type={"default"} className={"mt-4 p-4"}>
                    <H5>Special instructions</H5>
                    <H6>{collection.data.specialInstructions}</H6>
                  </ActionPanelContainer>
                )}
              </CardContent>
            </Card>
            <CollectionContactDetails />
          </div>
        )}
      </PageContainer>
      {collection.data && (
        <>
          <FinaliseCollectionDialog
            active={finaliseModalActive}
            collectionId={collection.data.id}
            collectionDate={collection.data.collectionDate}
            onDismiss={() => setFinaliseModalActive(false)}
          />
          <CancelCollectionDialog
            active={cancelModalActive}
            collectionId={collection.data.id}
            collectionDate={collection.data.collectionDate}
            onDismiss={() => setCancelModalActive(false)}
          />
        </>
      )}
    </>
  );
}

const OrderCard = ({
  order,
}: {
  order: CollectionGetResponseType["Orders"][number];
}) => {
  const navigate = useNavigate();

  const orderCollectionDetails = useCollectionOrderData(order.id);

  return (
    <div
      className="flex flex-none cursor-pointer flex-col items-center gap-2 rounded-lg border border-zinc-300 p-2 hover:bg-zinc-50"
      onClick={() => navigate(ORDER_DETAILS.href({ orderId: order.id }))}
    >
      <div className="flex w-full items-center justify-between text-sm text-zinc-500">
        Order #{order.id}
        <span className="font-semibold">{formatCurrency(order.price)}</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1">
            <ProfilePicture
              logoUrl={order.Retailer.Business.logoUrl}
              name={order.Retailer.Business.name}
              size="small"
              fallback="supplier-badge"
            />

            <span className="title">{order.Retailer.Business.name}</span>
          </div>
          <span className="text-xs text-secondary">
            Placed on: {dayjs(order.createdAt).format("DD/MM/YYYY")}
          </span>
          <div className="flex justify-between">
            <span className="text-xs text-secondary">
              No. of packages: {orderCollectionDetails.numberOfPackages}
            </span>
            <span className="text-xs text-secondary">
              Weight: {orderCollectionDetails.totalWeight}kg
            </span>
          </div>
        </div>
        <Icon icon="CHEVRON_RIGHT" />
      </div>
    </div>
  );
};
